import { useState } from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import { CopyButton, CodeTabs } from './CopyCode';

// ----------------------------------------------------------------------
// Modo "Automático" (identify): o site host informa quem é o usuário logado via
// LaisChat.identify(). A assinatura HMAC (gerada no backend do host com a secret
// abaixo) prova que o email é autêntico → selo verde no atendimento.

export default function ChannelWebchatIdentify({ secret }) {
    const { t } = useTranslation('settings');
    const [copiedMd, setCopiedMd] = useState(false);
    const secretExibida = secret || t('channels.webchat.identify.secretPlaceholder', 'SUA_SECRET_APARECE_AQUI_APOS_SALVAR');

    // Passo 1 — assinar o email do usuário logado, no BACKEND do host.
    const backendTabs = [
        {
            id: 'node',
            label: 'Node.js',
            lang: 'javascript',
            code: `const crypto = require("crypto");
const LAIS_SECRET = "${secretExibida}"; // ${t('channels.webchat.identify.envVarComment', 'use variável de ambiente!')}

function signEmail(email) {
  return crypto.createHmac("sha256", LAIS_SECRET).update(email).digest("hex");
}`,
        },
        {
            id: 'python',
            label: 'Python',
            lang: 'python',
            code: `import hmac, hashlib

LAIS_SECRET = "${secretExibida}"  # ${t('channels.webchat.identify.envVarComment', 'use variável de ambiente!')}

def sign_email(email: str) -> str:
    return hmac.new(LAIS_SECRET.encode(), email.encode(), hashlib.sha256).hexdigest()`,
        },
        {
            id: 'php',
            label: 'PHP',
            lang: 'php',
            code: `<?php
$laisSecret = "${secretExibida}"; // ${t('channels.webchat.identify.envVarComment', 'use variável de ambiente!')}

function signEmail($email) {
  global $laisSecret;
  return hash_hmac("sha256", $email, $laisSecret);
}`,
        },
        {
            id: 'ruby',
            label: 'Ruby',
            lang: 'ruby',
            code: `require "openssl"

LAIS_SECRET = "${secretExibida}" # ${t('channels.webchat.identify.envVarComment', 'use variável de ambiente!')}

def sign_email(email)
  OpenSSL::HMAC.hexdigest("SHA256", LAIS_SECRET, email)
end`,
        },
        {
            id: 'csharp',
            label: 'C#',
            lang: 'csharp',
            code: `using System.Security.Cryptography;
using System.Text;

var laisSecret = "${secretExibida}"; // ${t('channels.webchat.identify.envVarComment', 'use variável de ambiente!')}

string SignEmail(string email) {
  using var hmac = new HMACSHA256(Encoding.UTF8.GetBytes(laisSecret));
  var hash = hmac.ComputeHash(Encoding.UTF8.GetBytes(email));
  return Convert.ToHexString(hash).ToLower();
}`,
        },
    ];

    // Passo 2 — informar o usuário ao widget, no FRONTEND do host.
    const frontendTabs = [
        {
            id: 'html',
            label: 'HTML',
            lang: 'html',
            code: `<!-- ${t('channels.webchat.identify.htmlInjectComment', 'Injete os valores no servidor ao renderizar a página')} -->
<script>
  window.LaisChat && window.LaisChat.identify({
    email: "${t('channels.webchat.identify.sampleEmail', 'usuario@exemplo.com')}",
    name: "${t('channels.webchat.identify.sampleName', 'João')}",
    signature: "${t('channels.webchat.identify.signaturePlaceholder', 'ASSINATURA_GERADA_NO_SERVIDOR')}",
    // ${t('channels.webchat.identify.optionalAttrsComment', 'opcionais — NÃO cobertos pela assinatura (só o email é assinado)')}
    telefone: "${t('channels.webchat.identify.samplePhone', '11999998888')}",
    empresa: "${t('channels.webchat.identify.sampleCompany', 'Acme Ltda')}",
    userId: "${t('channels.webchat.identify.sampleUserId', 'u_123')}"
  });
</script>`,
        },
        {
            id: 'react',
            label: 'React',
            lang: 'jsx',
            code: `import { useEffect } from "react";

// ${t('channels.webchat.identify.reactBootComment', 'Uma vez, no boot do app (após o login):')}
useEffect(() => {
  fetch("/me")
    .then((r) => r.json())
    .then((user) => {
      window.LaisChat?.identify({
        email: user.email,
        name: user.name,
        signature: user.laisSignature, // ${t('channels.webchat.identify.fromYourBackendComment', 'vinda do seu backend')}
        // ${t('channels.webchat.identify.optionalAttrsComment', 'opcionais — NÃO cobertos pela assinatura (só o email é assinado)')}
        telefone: user.phone,
        empresa: user.company,
        userId: user.id,
      });
    });
}, []);`,
        },
        {
            id: 'vue',
            label: 'Vue',
            lang: 'javascript',
            code: `import { onMounted } from "vue";

// ${t('channels.webchat.identify.vueMountComment', 'No App.vue (Vue 3):')}
onMounted(async () => {
  const user = await fetch("/me").then((r) => r.json());
  window.LaisChat?.identify({
    email: user.email,
    name: user.name,
    signature: user.laisSignature, // ${t('channels.webchat.identify.fromYourBackendComment', 'vinda do seu backend')}
    // ${t('channels.webchat.identify.optionalAttrsComment', 'opcionais — NÃO cobertos pela assinatura (só o email é assinado)')}
    telefone: user.phone,
    empresa: user.company,
    userId: user.id,
  });
});`,
        },
        {
            id: 'angular',
            label: 'Angular',
            lang: 'typescript',
            code: `import { Component, OnInit } from "@angular/core";

@Component({ selector: "app-root", template: "" })
export class AppComponent implements OnInit {
  ngOnInit(): void {
    fetch("/me")
      .then((r) => r.json())
      .then((user) => {
        (window as any).LaisChat?.identify({
          email: user.email,
          name: user.name,
          signature: user.laisSignature, // ${t('channels.webchat.identify.fromYourBackendComment', 'vinda do seu backend')}
          // ${t('channels.webchat.identify.optionalAttrsComment', 'opcionais — NÃO cobertos pela assinatura (só o email é assinado)')}
          telefone: user.phone,
          empresa: user.company,
          userId: user.id,
        });
      });
  }
}`,
        },
    ];

    // Guia completo em Markdown — para o cliente colar num LLM e pedir ajuda a
    // integrar no stack dele. Reúne explicação + secret + todos os exemplos.
    const buildMarkdown = () => {
        const fence = (lang, code) => '```' + (lang || '') + '\n' + code + '\n```';
        const secao = (tabs) => tabs.map((tab) => `#### ${tab.label}\n\n${fence(tab.lang, tab.code)}`).join('\n\n');
        const secretMd = secret
            ? secret
            : t('channels.webchat.identify.secretMdPlaceholder', '(gere a chave salvando o projeto no modo "Automático" e cole aqui)');

        const guideDefault =
            '# Integração do widget de suporte LAIS — identificação do usuário logado\n\n' +
            'Estou integrando o widget de chat de suporte da **LAIS** no meu site/SaaS e quero **identificar automaticamente o usuário logado**, com verificação de identidade via assinatura **HMAC-SHA256** (padrão parecido com o do Crisp). Preciso da sua ajuda para implementar isso no meu stack.\n\n' +
            '## Como funciona\n' +
            '- O widget é carregado por um `<script>` e expõe o objeto global `window.LaisChat`.\n' +
            '- Após o login do usuário no meu site, meu **frontend** chama `LaisChat.identify({ email, name, signature, telefone?, empresa?, userId?, accountId? })`.\n' +
            '- A `signature` é o **HMAC-SHA256 do email**, em hex, gerada no **meu backend** com uma chave secreta compartilhada (nunca no frontend). Cobre **só o email** — os demais atributos (telefone/empresa/userId/accountId) são opcionais e chegam como metadados **não verificados**, só para dar contexto ao atendente.\n' +
            '- Se a assinatura confere, o email aparece como **verificado** no painel de atendimento da LAIS.\n' +
            '- No **logout**, meu frontend chama `LaisChat.reset()` para limpar a identidade e zerar a sessão local — no próximo login com o mesmo email verificado, o histórico da conversa volta automaticamente (inclusive de outro navegador/dispositivo).\n\n' +
            '## Chave secreta (manter SOMENTE no backend, em variável de ambiente)\n' +
            '```\n' +
            '{{secretBlock}}\n' +
            '```\n\n' +
            '## Passo 1 — Backend: assinar o email do usuário logado\n' +
            'Gere `signature = HMAC_SHA256(secret, email)` em hexadecimal. Exemplos por linguagem:\n\n' +
            '{{backendExamples}}\n\n' +
            '## Passo 2 — Frontend: informar o usuário ao widget\n' +
            'Chame `LaisChat.identify()` uma vez após o login (o widget persiste e reenvia sozinho nas reconexões). Exemplos por framework:\n\n' +
            '{{frontendExamples}}\n\n' +
            '## Regras importantes\n' +
            '- A chave secreta **nunca** pode aparecer no código do frontend — só no backend.\n' +
            '- A assinatura deve cobrir **exatamente** o mesmo email passado no `identify()`.\n' +
            '- **Sem assinatura**: o usuário ainda é identificado (nome/email aparecem), mas sem o selo de verificado.\n' +
            '- **Visitante não logado**: não chame `identify()` — ele conversa como anônimo.\n' +
            '- **Logout**: chame `LaisChat.reset()`.\n\n' +
            '## O que eu preciso de você\n' +
            'Meu backend é: **[descreva: linguagem/framework]**. Meu frontend é: **[descreva: framework]**.\n' +
            'Me ajude a implementar a geração da assinatura no backend e a chamada `identify()`/`reset()` no frontend, adaptando os exemplos acima ao meu stack.';

        return t('channels.webchat.identify.integrationGuideMd', guideDefault, {
            secretBlock: secretMd,
            backendExamples: secao(backendTabs),
            frontendExamples: secao(frontendTabs),
        });
    };

    return (
        <div className="flex flex-col gap-5 mt-2 animate-in fade-in duration-300">
            {/* Copiar guia para IA */}
            <div className="flex items-center justify-between gap-3 bg-slate-900 rounded-xl p-4">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-white/10 flex-shrink-0">
                        <Iconify icon="solar:magic-stick-3-bold" width={18} className="text-white" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-bold text-white">{t('channels.webchat.identify.aiHelperTitle', 'Integrar com ajuda de IA')}</p>
                        <p className="text-xs text-slate-300 truncate">
                            {t('channels.webchat.identify.aiHelperSubtitle', 'Copie um guia completo em Markdown e cole no ChatGPT, Claude, etc.')}
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => {
                        navigator.clipboard.writeText(buildMarkdown());
                        setCopiedMd(true);
                        setTimeout(() => setCopiedMd(false), 2500);
                    }}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white text-slate-900 text-sm font-semibold hover:bg-slate-100 transition-colors whitespace-nowrap flex-shrink-0"
                >
                    <Iconify icon={copiedMd ? 'eva:checkmark-outline' : 'eva:copy-outline'} width={16} className={copiedMd ? 'text-green-600' : ''} />
                    {copiedMd
                        ? t('channels.webchat.identify.copiedLabel', 'Copiado!')
                        : t('channels.webchat.identify.copyForAiButton', 'Copiar para IA')}
                </button>
            </div>

            {/* Como funciona */}
            <div className="bg-violet-50/50 rounded-xl border border-violet-100 p-5">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-100 flex-shrink-0">
                        <Iconify icon="solar:shield-check-bold" width={20} className="text-violet-600" />
                    </div>
                    <div className="text-sm text-gray-700 leading-relaxed">
                        <p className="font-bold text-gray-900 mb-1">{t('channels.webchat.identify.howItWorksTitle', 'Como funciona')}</p>
                        <p>
                            <Trans
                                t={t}
                                i18nKey="channels.webchat.identify.howItWorksBody"
                                components={{ b: <b />, bGreen: <b className="text-green-600" /> }}
                            >
                                Ideal para sites/SaaS com <b>usuário logado</b>. Seu site informa ao widget quem é o
                                visitante — sem formulário e sem fricção. Se você também enviar a <b>assinatura</b>
                                (gerada no seu backend com a chave abaixo), o email aparece com um{' '}
                                <bGreen>selo verde de verificado</bGreen> no atendimento, provando que
                                não foi forjado.
                            </Trans>
                        </p>
                    </div>
                </div>
            </div>

            {/* Secret */}
            <div className="bg-white rounded-xl border border-gray-100 p-5 flex flex-col gap-3">
                <p className="text-sm font-bold text-gray-900">{t('channels.webchat.identify.secretKeyTitle', 'Chave secreta (Secret Key)')}</p>
                {secret ? (
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            readOnly
                            value={secret}
                            className="flex-1 border border-gray-200 bg-gray-50 rounded-xl px-3 py-2 text-sm font-mono text-gray-700 outline-none"
                        />
                        <CopyButton text={secret} />
                    </div>
                ) : (
                    <p className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                        <Trans t={t} i18nKey="channels.webchat.identify.secretPendingHint" components={{ b: <b /> }}>
                            A chave é gerada automaticamente ao <b>salvar o projeto</b> com este modo ativo.
                        </Trans>
                    </p>
                )}
                <p className="text-[11px] text-gray-400">
                    ⚠️{' '}
                    <Trans t={t} i18nKey="channels.webchat.identify.secretWarning" components={{ b: <b /> }}>
                        Mantenha essa chave <b>apenas no seu backend</b> (variável de ambiente). Nunca a coloque no
                        código do frontend — quem tiver a chave consegue forjar identidades.
                    </Trans>
                </p>
            </div>

            {/* Passo 1: backend assina */}
            <div className="bg-white rounded-xl border border-gray-100 p-5 flex flex-col gap-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t('channels.webchat.identify.backendStepTitle', '1. No seu backend — assine o email do usuário logado')}
                </p>
                <CodeTabs tabs={backendTabs} />
            </div>

            {/* Passo 2: frontend chama identify */}
            <div className="bg-white rounded-xl border border-gray-100 p-5 flex flex-col gap-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {t('channels.webchat.identify.frontendStepTitle', '2. No seu site — informe o usuário ao widget')}
                </p>
                <CodeTabs tabs={frontendTabs} />
            </div>

            {/* Observações */}
            <div className="bg-gray-50 rounded-xl border border-gray-100 p-4">
                <p className="text-xs text-gray-500 leading-relaxed">
                    <Trans
                        t={t}
                        i18nKey="channels.webchat.identify.notesBody"
                        components={{
                            b: <b />,
                            code: <code className="font-mono text-violet-600" />,
                        }}
                    >
                        <b>Sem assinatura</b>: o usuário ainda é identificado (nome/email aparecem), mas <b>sem</b> o selo
                        de verificado. <b>Visitante não logado</b>: nenhuma identificação é enviada — ele conversa como
                        anônimo. Chame <code>LaisChat.identify()</code> uma vez após
                        o login; o widget guarda os dados e reenvia sozinho nas reconexões. No <b>logout</b>, chame{' '}
                        <code>LaisChat.reset()</code> para limpar a identidade.
                    </Trans>
                </p>
            </div>
        </div>
    );
}

ChannelWebchatIdentify.propTypes = {
    secret: PropTypes.string,
};
